import React, { useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@radix-ui/react-popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from 'cmdk';
import { Check, ChevronsUpDown, CircleX, Search } from 'lucide-react';

export interface LocalSelectOption {
  value: string | number;
  label: string;
}

export interface LocalSelectProps {
  options: LocalSelectOption[];
  /**
   * Valor seleccionado actualmente
   */
  value?: string | number;
  /**
   * Callback ejecutado al seleccionar un elemento o al limpiar.
   */
  onChange: (value: string | number) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  className?: string;
  /**
   * Determina si se muestra el botón de limpiar (X) cuando hay selección
   */
  allowClear?: boolean;
}

/**
 * LocalSelect
 * Componente reutilizable para seleccionar elementos de opciones fijas.
 * Visualmente idéntico a CatalogSelect para mantener consistencia.
 */
export function LocalSelect({
  options,
  value,
  onChange,
  placeholder = 'Seleccionar...',
  searchPlaceholder = 'Buscar...',
  className = '',
  allowClear = true,
}: LocalSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const selectedOption = options.find((opt) => String(opt.value) === String(value));

  const filteredOptions = options.filter((opt) =>
    opt.label.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-expanded={open}
          className={`flex h-10 w-full items-center justify-between rounded-md border border-slate-200 bg-white px-3 py-2 text-sm ring-offset-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-950 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
        >
          <span className="truncate">
            {selectedOption ? (
              selectedOption.label
            ) : (
              <span className="text-slate-500">{placeholder}</span>
            )}
          </span>
          {selectedOption && allowClear ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onChange('');
                setSearch('');
                setOpen(false);
              }}
              className="p-1"
            >
              <CircleX className="h-4 w-4 text-slate-500 hover:text-slate-700" />
            </button>
          ) : (
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0 z-50 bg-white border border-slate-200 rounded-md shadow-md"
        align="start"
      >
        <Command className="w-full rounded-md" shouldFilter={false}>
          <div className="flex items-center border-b px-3">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <CommandInput
              placeholder={searchPlaceholder}
              value={search}
              onValueChange={setSearch}
              className="flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-slate-500 disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>
          <CommandList className="max-h-60 overflow-y-auto p-1">
            {filteredOptions.length === 0 && (
              <CommandEmpty className="py-6 text-center text-sm text-slate-500">
                No se encontraron resultados.
              </CommandEmpty>
            )}

            <CommandGroup>
              {filteredOptions.map((opt) => {
                const isSelected = String(value) === String(opt.value);

                return (
                  <CommandItem
                    key={opt.value}
                    value={String(opt.value)}
                    onSelect={() => {
                      onChange(opt.value);
                      setOpen(false);
                    }}
                    className={`relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50 data-[selected=true]:bg-slate-100 data-[selected=true]:text-slate-900 ${
                      isSelected ? 'bg-slate-100 text-slate-900 font-medium' : ''
                    }`}
                  >
                    <Check
                      className={`mr-2 h-4 w-4 ${isSelected ? 'opacity-100' : 'opacity-0'}`}
                    />
                    {opt.label}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
